/**
 * Custom Authenticator Wrapper
 *
 * Wraps Amplify UI Authenticator with custom styling matching the casino theme.
 * Supports:
 * - Password auth (SRP)
 * - Email OTP
 * - WebAuthn passkeys
 */

import { Authenticator, useAuthenticator, ThemeProvider } from '@aws-amplify/ui-react';
import type { JSX, ReactNode } from 'react';

import '@aws-amplify/ui-react/styles.css';
import './auth-styles.css';

/**
 * Custom theme tokens for the casino theme.
 * Matches the RMG dark premium style.
 */
const casinoTheme = {
  name: 'casino-theme',
  tokens: {
    colors: {
      background: {
        primary: { value: '#0a0510' },
        secondary: { value: 'rgba(20, 15, 30, 0.9)' },
      },
      font: {
        primary: { value: '#f8f9fa' },
        secondary: { value: '#a098b0' },
        interactive: { value: '#eab308' },
      },
      brand: {
        primary: {
          10: { value: '#1a1510' },
          20: { value: '#332a20' },
          40: { value: '#665430' },
          60: { value: '#997f40' },
          80: { value: '#ccaa50' },
          90: { value: '#e6c060' },
          100: { value: '#eab308' },
        },
      },
      border: {
        primary: { value: 'rgba(255, 215, 0, 0.2)' },
        secondary: { value: 'rgba(255, 215, 0, 0.5)' },
      },
    },
    components: {
      authenticator: {
        router: {
          backgroundColor: { value: 'transparent' },
          borderWidth: { value: '0' },
        },
      },
      button: {
        primary: {
          backgroundColor: { value: '#eab308' },
          color: { value: '#1a1a1a' },
          _hover: {
            backgroundColor: { value: '#facc15' },
          },
          _active: {
            backgroundColor: { value: '#ca8a04' },
          },
        },
        link: {
          color: { value: '#eab308' },
        },
      },
      fieldcontrol: {
        backgroundColor: { value: 'rgba(0, 0, 0, 0.5)' },
        borderColor: { value: 'rgba(255, 215, 0, 0.2)' },
        color: { value: '#f8f9fa' },
        _focus: {
          borderColor: { value: '#eab308' },
        },
      },
      tabs: {
        item: {
          color: { value: '#a098b0' },
          _active: {
            color: { value: '#eab308' },
            borderColor: { value: '#eab308' },
          },
          _hover: {
            color: { value: '#facc15' },
          },
        },
      },
    },
    radii: {
      small: { value: '8px' },
      medium: { value: '12px' },
      large: { value: '16px' },
    },
  },
};

/**
 * Custom header component for the authenticator.
 */
function AuthHeader(): JSX.Element {
  return (
    <div className="auth-header">
      <div className="auth-logo">
        <span className="auth-logo-r">R</span>MG
      </div>
      <p className="auth-subtitle">Sign in to play</p>
    </div>
  );
}

/**
 * Custom footer component for the authenticator.
 */
function AuthFooter(): JSX.Element {
  return (
    <div className="auth-footer">
      <p>18+ only. Please gamble responsibly.</p>
    </div>
  );
}

interface IAuthenticatorWrapperProps {
  children: ReactNode;
}

/**
 * Main authenticator wrapper component.
 * Wraps the app with auth protection using custom casino styling.
 */
export function AuthenticatorWrapper({ children }: IAuthenticatorWrapperProps): ReactNode {
  return (
    <ThemeProvider theme={casinoTheme}>
      <Authenticator
        components={{
          Header: AuthHeader,
          Footer: AuthFooter,
        }}
        hideSignUp={true}
        variation="modal"
      >
        {children}
      </Authenticator>
    </ThemeProvider>
  );
}

/**
 * Hook to access auth state and user.
 */
export { useAuthenticator };
